import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/admin/ui/table';
import { Input } from '@/components/admin/ui/input';
import { Badge } from '@/components/admin/ui/badge';
import { Button } from '@/components/admin/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/admin/ui/alert-dialog';
import {
  Search, Users, DollarSign, Clock, CheckCircle2,
  XCircle, Download, AlertTriangle, Building2, FileText,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { consoleLink } from '@/lib/staging/consoleLink';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AnalyticsRow {
  referrer_id:         string;
  referrer_name:       string;
  referrer_email:      string;
  referral_code:       string | null;
  total_sent:          number;
  total_converted:     number;
  conversion_rate_pct: number | null;
  payouts_pending:     number;
  payouts_success:     number;
  payouts_completed:   number;
  payouts_void:        number;
  total_paid_cents:    number;
  total_owed_cents:    number;
  last_sent_at:        string;
  w9_submitted:        boolean | null;
  banking_info_added:  boolean | null;
  qbo_info_sent:       boolean | null;
}

interface ReferralRow {
  id:                   string;
  referee_id:           string | null;
  referee_email:        string;
  referred_client_name: string | null;
  status:               string;
  payout_status:        string | null;
  booking_confirmed_at: string | null;
  sent_at:              string;
  success_at:           string | null;
  completed_at:         string | null;
  voided_at:            string | null;
  void_reason:          string | null;
  payout_amount:        number;
}

// ── Config ────────────────────────────────────────────────────────────────────

const PAYOUT_BADGE: Record<string, string> = {
  pending:   'bg-amber-100 text-amber-700 border-amber-200',
  success:   'bg-green-100 text-green-700 border-green-200',
  completed: 'bg-blue-100 text-blue-700 border-blue-200',
  void:      'bg-gray-100 text-gray-500 border-gray-200',
};

const PAYOUT_FILTER_OPTIONS = ['all', 'pending', 'success', 'completed', 'void'];

const fmt = (date: string | null) =>
  date ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const fmtDollars = (cents: number) => `$${(cents / 100).toFixed(0)}`;

// ── Component ─────────────────────────────────────────────────────────────────

const ReferralAnalytics = () => {
  const [analytics, setAnalytics]             = useState<AnalyticsRow[]>([]);
  const [loading, setLoading]                 = useState(true);
  const [search, setSearch]                   = useState('');
  const [payoutFilter, setPayoutFilter]       = useState('all');
  const [selectedReferrer, setSelectedReferrer] = useState<string | null>(null);
  const [drillReferrals, setDrillReferrals]   = useState<ReferralRow[]>([]);
  const [drillLoading, setDrillLoading]       = useState(false);
  const [actionLoading, setActionLoading]     = useState<string | null>(null);
  const [pendingAction, setPendingAction]     = useState<{ type: 'mark_paid' | 'void'; referralId: string; isPending: boolean } | null>(null);
  const [voidReason, setVoidReason]           = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabaseAdmin
        .from('referral_analytics')
        .select('*')
        .order('total_paid_cents', { ascending: false });
      if (error) throw error;
      setAnalytics(data ?? []);
    } catch (err: any) {
      toast({ title: 'Error loading referral data', description: err.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // ── Summary totals ────────────────────────────────────────────────────────

  const totals = useMemo(() => ({
    referrers:   analytics.length,
    pending:     analytics.reduce((s, r) => s + (r.payouts_pending ?? 0), 0),
    success:     analytics.reduce((s, r) => s + (r.payouts_success ?? 0), 0),
    completed:   analytics.reduce((s, r) => s + (r.payouts_completed ?? 0), 0),
    totalPaid:   analytics.reduce((s, r) => s + (r.total_paid_cents ?? 0), 0),
    totalOwed:   analytics.reduce((s, r) => s + (r.total_owed_cents ?? 0), 0),
    needs1099:   analytics.filter(r => (r.total_paid_cents ?? 0) >= 60000).length,
  }), [analytics]);

  // ── Filtered table ────────────────────────────────────────────────────────

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return analytics.filter(r => {
      const matchSearch = !q || r.referrer_name?.toLowerCase().includes(q) || r.referrer_email?.toLowerCase().includes(q);
      const matchPayout =
        payoutFilter === 'all' ||
        (payoutFilter === 'pending'   && (r.payouts_pending ?? 0) > 0) ||
        (payoutFilter === 'success'   && (r.payouts_success ?? 0) > 0) ||
        (payoutFilter === 'completed' && (r.payouts_completed ?? 0) > 0) ||
        (payoutFilter === 'void'      && (r.payouts_void ?? 0) > 0);
      return matchSearch && matchPayout;
    });
  }, [analytics, search, payoutFilter]);

  // ── Drill-down ────────────────────────────────────────────────────────────

  const openDrill = async (referrerId: string) => {
    setSelectedReferrer(referrerId);
    setDrillLoading(true);
    try {
      const { data: { session } } = await supabaseAdmin.auth.getSession();
      const res = await fetch(`/api/promotions/referral-drill-down/${referrerId}`, {
        headers: { Authorization: `Bearer ${session?.access_token || ''}` },
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load referrals');
      setDrillReferrals(json.referrals ?? []);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDrillLoading(false);
    }
  };

  // ── Admin actions ─────────────────────────────────────────────────────────

  // Opens the confirmation dialog — actual DB call happens in confirmMarkComplete
  const markComplete = (referralId: string, isPending: boolean) => {
    setPendingAction({ type: 'mark_paid', referralId, isPending });
  };

  const confirmMarkComplete = async () => {
    const referralId = pendingAction?.referralId;
    if (!referralId) return;
    setPendingAction(null);
    setActionLoading(referralId);
    try {
      const { data: { session } } = await supabaseAdmin.auth.getSession();
      const res = await fetch('/api/promotions/referral-update-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
        body: JSON.stringify({ referral_id: referralId, action: 'mark_paid' }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to update');
      toast({ title: 'Marked as paid out' });
      const now = new Date().toISOString();
      setDrillReferrals(prev => prev.map(r =>
        r.id === referralId ? { ...r, payout_status: 'completed', completed_at: now } : r
      ));
      await fetchData();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  // Opens the void dialog — actual DB call happens in confirmVoid
  const markVoid = (referralId: string) => {
    setVoidReason('');
    setPendingAction({ type: 'void', referralId, isPending: false });
  };

  const confirmVoid = async () => {
    const referralId = pendingAction?.referralId;
    if (!referralId) return;
    const reason = voidReason.trim() || 'admin_void';
    setPendingAction(null);
    setActionLoading(referralId);
    try {
      const { data: { session } } = await supabaseAdmin.auth.getSession();
      const res = await fetch('/api/promotions/referral-update-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
        body: JSON.stringify({ referral_id: referralId, action: 'void', void_reason: reason }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to update');
      toast({ title: 'Referral voided' });
      setDrillReferrals(prev => prev.map(r =>
        r.id === referralId ? { ...r, payout_status: 'void', voided_at: new Date().toISOString(), void_reason: reason } : r
      ));
      await fetchData();
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setActionLoading(null);
    }
  };

  const toggleQboField = async (userId: string, field: 'w9_submitted' | 'banking_info_added' | 'qbo_info_sent', newVal: boolean) => {
    try {
      const { data: { session } } = await supabaseAdmin.auth.getSession();
      const res = await fetch('/api/promotions/referral-payee-toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token || ''}` },
        body: JSON.stringify({ user_id: userId, field, value: newVal }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Update failed');
      setAnalytics(prev => prev.map(r =>
        r.referrer_id === userId ? { ...r, [field]: newVal } : r
      ));
      toast({ title: field === 'w9_submitted' ? `W-9 marked as ${newVal ? 'received' : 'pending'}` : `Banking info marked as ${newVal ? 'on file' : 'pending'}` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  // ── CSV export ────────────────────────────────────────────────────────────

  const exportCSV = () => {
    const headers = ['Name','Email','Code','Converted','Pending','Success','Paid','Total Paid','Total Owed','QBO Info','W-9','Banking'];
    const rows = filtered.map(r => [
      r.referrer_name, r.referrer_email, r.referral_code ?? '',
      r.total_converted, r.payouts_pending, r.payouts_success, r.payouts_completed,
      (r.total_paid_cents / 100).toFixed(0),
      (r.total_owed_cents / 100).toFixed(0),
      r.qbo_info_sent ? 'Sent' : 'Pending',
      r.w9_submitted ? 'Yes' : 'No',
      r.banking_info_added ? 'Yes' : 'No',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
      download: `referral-analytics-${new Date().toISOString().slice(0,10)}.csv`,
    });
    a.click();
  };

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="p-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Referral Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Payout lifecycle management — Share the Love program</p>
        </div>
        <Button variant="outline" size="sm" onClick={exportCSV} className="flex items-center gap-2">
          <Download className="w-4 h-4" /> Export CSV
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[
          { label: 'Referrers',    value: totals.referrers,            icon: Users,        color: 'text-blue-600'  },
          { label: 'Pending',      value: totals.pending,              icon: Clock,        color: 'text-amber-600' },
          { label: 'Ready to Pay', value: totals.success,              icon: CheckCircle2, color: 'text-green-600' },
          { label: 'Paid Out',     value: totals.completed,            icon: DollarSign,   color: 'text-blue-600'  },
          { label: 'Total Paid',   value: fmtDollars(totals.totalPaid),icon: DollarSign,   color: 'text-green-700' },
          { label: 'Total Owed',   value: fmtDollars(totals.totalOwed),icon: AlertTriangle,color: 'text-amber-600' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <Card key={s.label}>
              <CardContent className="pt-4 pb-4 text-center">
                <Icon className={`w-4 h-4 ${s.color} mx-auto mb-1`} />
                <p className="text-xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* 1099-NEC alert */}
      {totals.needs1099 > 0 && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <FileText className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">1099-NEC Threshold Reached</p>
            <p className="text-sm text-amber-700 mt-0.5">
              {totals.needs1099} referrer{totals.needs1099 !== 1 ? 's have' : ' has'} earned $600+ this year. Issue a 1099-NEC for each.
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search name or email..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 h-9 text-sm"
          />
        </div>
        <div className="flex gap-1">
          {PAYOUT_FILTER_OPTIONS.map(f => (
            <button
              key={f}
              onClick={() => setPayoutFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors capitalize ${
                payoutFilter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {f === 'all' ? 'All' : f}
            </button>
          ))}
        </div>
      </div>

      {/* Main table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">By Referrer</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground text-sm">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground text-sm">No referral data.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Referrer</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead className="text-center">Pending</TableHead>
                  <TableHead className="text-center">Ready</TableHead>
                  <TableHead className="text-center">Paid</TableHead>
                  <TableHead className="text-right">Total Paid</TableHead>
                  <TableHead className="text-right">Owed</TableHead>
                  <TableHead className="text-center">QBO Info</TableHead>
                  <TableHead className="text-center">W-9</TableHead>
                  <TableHead className="text-center">Banking</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(row => (
                  <TableRow key={row.referrer_id}>
                    <TableCell>
                      <button
                        onClick={() => navigate(consoleLink(`/lp-console/clients/${row.referrer_id}`))}
                        className="font-medium text-sm text-primary hover:underline text-left"
                      >
                        {row.referrer_name}
                      </button>
                      <p className="text-xs text-muted-foreground">{row.referrer_email}</p>
                      {(row.total_paid_cents ?? 0) >= 60000 && (
                        <span className="text-xs text-amber-600 font-medium">1099-NEC required</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.referral_code
                        ? <span className="font-mono text-sm font-medium">{row.referral_code}</span>
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`text-sm font-medium ${(row.payouts_pending ?? 0) > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                        {row.payouts_pending ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={`text-sm font-medium ${(row.payouts_success ?? 0) > 0 ? 'text-green-600 font-bold' : 'text-muted-foreground'}`}>
                        {row.payouts_success ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-center text-sm">{row.payouts_completed ?? 0}</TableCell>
                    <TableCell className="text-right text-sm font-medium text-green-700">
                      {fmtDollars(row.total_paid_cents ?? 0)}
                    </TableCell>
                    <TableCell className="text-right text-sm text-amber-600 font-medium">
                      {(row.total_owed_cents ?? 0) > 0 ? fmtDollars(row.total_owed_cents) : '—'}
                    </TableCell>
                    {/* QBO Info select */}
                    <TableCell className="text-center">
                      <div className={`relative inline-flex items-center rounded transition-colors ${
                        row.qbo_info_sent
                          ? 'bg-green-100 border-2 border-green-500'
                          : 'bg-gray-50 border border-gray-300'
                      }`}>
                        <select
                          value={row.qbo_info_sent ? 'sent' : 'pending'}
                          onChange={e => toggleQboField(row.referrer_id, 'qbo_info_sent', e.target.value === 'sent')}
                          className="appearance-none bg-transparent text-xs font-medium cursor-pointer pl-2 pr-6 py-1 focus:outline-none"
                          style={{ color: row.qbo_info_sent ? '#166534' : '#6b7280' }}
                        >
                          <option value="pending">Pending</option>
                          <option value="sent">Sent</option>
                        </select>
                        <ChevronDown className="absolute right-1 w-3 h-3 pointer-events-none" style={{ color: row.qbo_info_sent ? '#166534' : '#6b7280' }} />
                      </div>
                    </TableCell>
                    {/* W-9 select */}
                    <TableCell className="text-center">
                      <div className={`relative inline-flex items-center rounded transition-colors ${
                        row.w9_submitted
                          ? 'bg-green-100 border-2 border-green-500'
                          : 'bg-gray-50 border border-gray-300'
                      }`}>
                        <select
                          value={row.w9_submitted ? 'received' : 'pending'}
                          onChange={e => toggleQboField(row.referrer_id, 'w9_submitted', e.target.value === 'received')}
                          className="appearance-none bg-transparent text-xs font-medium cursor-pointer pl-2 pr-6 py-1 focus:outline-none"
                          style={{ color: row.w9_submitted ? '#166534' : '#6b7280' }}
                        >
                          <option value="pending">Pending</option>
                          <option value="received">Received</option>
                        </select>
                        <ChevronDown className="absolute right-1 w-3 h-3 pointer-events-none" style={{ color: row.w9_submitted ? '#166534' : '#6b7280' }} />
                      </div>
                    </TableCell>
                    {/* Banking select */}
                    <TableCell className="text-center">
                      <div className={`relative inline-flex items-center rounded transition-colors ${
                        row.banking_info_added
                          ? 'bg-green-100 border-2 border-green-500'
                          : 'bg-gray-50 border border-gray-300'
                      }`}>
                        <select
                          value={row.banking_info_added ? 'on_file' : 'pending'}
                          onChange={e => toggleQboField(row.referrer_id, 'banking_info_added', e.target.value === 'on_file')}
                          className="appearance-none bg-transparent text-xs font-medium cursor-pointer pl-2 pr-6 py-1 focus:outline-none"
                          style={{ color: row.banking_info_added ? '#166534' : '#6b7280' }}
                        >
                          <option value="pending">Pending</option>
                          <option value="on_file">On file</option>
                        </select>
                        <ChevronDown className="absolute right-1 w-3 h-3 pointer-events-none" style={{ color: row.banking_info_added ? '#166534' : '#6b7280' }} />
                      </div>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => selectedReferrer === row.referrer_id ? setSelectedReferrer(null) : openDrill(row.referrer_id)}
                        className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                      >
                        {selectedReferrer === row.referrer_id
                          ? <ChevronUp className="w-4 h-4" />
                          : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Drill-down panel */}
      {selectedReferrer && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-base">
              Referrals — {analytics.find(r => r.referrer_id === selectedReferrer)?.referrer_name}
            </CardTitle>
            <button onClick={() => setSelectedReferrer(null)} className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground">
              <ChevronUp className="w-4 h-4" />
            </button>
          </CardHeader>
          <CardContent className="p-0">
            {drillLoading ? (
              <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>
            ) : drillReferrals.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">No referrals.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Client</TableHead>
                    <TableHead>Payout Status</TableHead>
                    <TableHead>Payout</TableHead>
                    <TableHead>Booking Date</TableHead>
                    <TableHead>Day 31 Passed</TableHead>
                    <TableHead>Paid Out</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {drillReferrals.map(r => (
                    <TableRow key={r.id}>
                      <TableCell>
                        {r.referee_id ? (
                          <button
                            onClick={() => navigate(consoleLink(`/lp-console/clients/${r.referee_id}`))}
                            className="text-sm font-medium text-primary hover:underline text-left"
                          >
                            {r.referred_client_name || r.referee_email}
                          </button>
                        ) : (
                          <p className="text-sm font-medium">{r.referred_client_name || '—'}</p>
                        )}
                        <p className="text-xs text-muted-foreground font-mono">{r.referee_email}</p>
                      </TableCell>
                      <TableCell>
                        {r.payout_status ? (
                          <span className={`inline-flex text-xs font-medium border px-2 py-0.5 rounded-full ${PAYOUT_BADGE[r.payout_status] ?? ''}`}>
                            {r.payout_status === 'success' ? 'Ready to Pay' : r.payout_status.replace('_', ' ')}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">{r.status.replace('_', ' ')}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm font-medium">
                        {fmtDollars(r.payout_amount ?? 20000)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmt(r.booking_confirmed_at)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmt(r.success_at)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{fmt(r.completed_at)}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {(r.payout_status === 'pending' || r.payout_status === 'success') && (
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={actionLoading === r.id}
                              onClick={() => markComplete(r.id, r.payout_status === 'pending')}
                              className="text-xs h-7 text-green-700 border-green-300 hover:bg-green-50"
                            >
                              Mark Paid
                            </Button>
                          )}
                          {(r.payout_status === 'pending' || r.payout_status === 'success') && (
                            <Button
                              variant="ghost"
                              size="sm"
                              disabled={actionLoading === r.id}
                              onClick={() => markVoid(r.id)}
                              className="text-xs h-7 text-red-600 hover:bg-red-50"
                            >
                              Void
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
      {/* Mark Paid confirmation dialog */}
      <AlertDialog open={pendingAction?.type === 'mark_paid'} onOpenChange={open => !open && setPendingAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark Referral as Paid Out</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction?.isPending
                ? 'This referral has not yet reached 31 days. Are you sure you want to mark it as paid out anyway?'
                : 'Confirm that you have processed this payout. This will mark the referral as completed.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmMarkComplete}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Mark Paid Out
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Void confirmation dialog */}
      <AlertDialog open={pendingAction?.type === 'void'} onOpenChange={open => !open && setPendingAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Void Referral</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The referral will be marked void and no payout will be issued.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-1 pb-2">
            <label className="text-sm font-medium text-foreground block mb-1.5">Reason</label>
            <Input
              placeholder="e.g. booking_cancelled, chargeback, duplicate"
              value={voidReason}
              onChange={e => setVoidReason(e.target.value)}
              className="text-sm"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmVoid}
              className="border border-red-400 text-red-600 bg-transparent hover:bg-red-50"
            >
              Void Referral
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReferralAnalytics;
